/**
 * Create / edit company profile.
 * Supports edit via `?id={id}` query param — same form is reused, mirroring
 * the NewInvestorPage pattern. The CompanyDetailPage's "Edit" button links
 * here with the company's id; the Companies index links here for create.
 *
 * Scope: fields a human actually edits manually. The richer JSON columns
 * (key_team_members, previous_funding, risks, key_metrics, patents) are
 * intentionally left to the brochure-extraction flow + dedicated editors,
 * since hand-editing nested JSON in a generic form is error-prone.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Save, UploadCloud, FileText, CheckCircle2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api, { apiDirect } from '../services/api';
import dataService from '../services/data';

const FUNDING_STAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'pre_seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series_a', label: 'Series A' },
  { value: 'series_b', label: 'Series B' },
  { value: 'series_c', label: 'Series C' },
  { value: 'growth', label: 'Growth' },
  { value: 'late_stage', label: 'Late Stage' },
  { value: 'bootstrap', label: 'Bootstrap' },
];

interface FormState {
  // Identity
  name: string;
  website: string;
  industry: string;
  sub_industry: string;
  description: string;
  founding_date: string;       // YYYY-MM-DD for the date input
  headquarters: string;
  location_state: string;
  employee_count: string;
  contact_name: string;
  contact_email: string;

  // Funding
  funding_stage: string;
  funding_amount_sought: string;
  valuation: string;
  previous_funding_raw: string;
  is_currently_raising: 'yes' | 'no' | 'unknown';
  fundraising_status_reason: string;

  // Financials
  current_revenue: string;
  monthly_revenue: string;
  revenue_growth_rate: string;
  gross_margin: string;
  burn_rate: string;
  runway_months: string;
  customer_acquisition_cost: string;
  lifetime_value: string;

  // Operations
  business_model: string;
  target_market: string;
  revenue_model: string;
  team_size: string;
  competitive_advantages_csv: string;

  // Context
  executive_summary: string;
  founder_demographics: string;
  university_affiliations: string;
  pitch_deck_url: string;

  // Organization
  group_name: string;
  new_group_name: string;
  group_mode: 'existing' | 'new';
  tags_csv: string;
}

const emptyForm: FormState = {
  name: '',
  website: '',
  industry: '',
  sub_industry: '',
  description: '',
  founding_date: '',
  headquarters: '',
  location_state: '',
  employee_count: '',
  contact_name: '',
  contact_email: '',
  funding_stage: '',
  funding_amount_sought: '',
  valuation: '',
  previous_funding_raw: '',
  is_currently_raising: 'unknown',
  fundraising_status_reason: '',
  current_revenue: '',
  monthly_revenue: '',
  revenue_growth_rate: '',
  gross_margin: '',
  burn_rate: '',
  runway_months: '',
  customer_acquisition_cost: '',
  lifetime_value: '',
  business_model: '',
  target_market: '',
  revenue_model: '',
  team_size: '',
  competitive_advantages_csv: '',
  executive_summary: '',
  founder_demographics: '',
  university_affiliations: '',
  pitch_deck_url: '',
  group_name: '',
  new_group_name: '',
  group_mode: 'existing',
  tags_csv: '',
};

const csvToArray = (value: string): string[] =>
  value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const arrayToCsv = (value?: string[] | null): string =>
  Array.isArray(value) ? value.join(', ') : '';

const toNumber = (value: string): number | undefined => {
  if (value === '' || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const trimOrUndef = (value: string): string | undefined =>
  value.trim() ? value.trim() : undefined;

const companyToForm = (c: any): FormState => ({
  name: c.name || '',
  website: c.website || '',
  industry: c.industry || '',
  sub_industry: c.sub_industry || '',
  description: c.description || '',
  founding_date: c.founding_date ? String(c.founding_date).slice(0, 10) : '',
  headquarters: c.headquarters || '',
  location_state: c.location_state || '',
  employee_count: c.employee_count != null ? String(c.employee_count) : '',
  contact_name: c.contact_name || '',
  contact_email: c.contact_email || '',
  funding_stage: c.funding_stage || '',
  funding_amount_sought:
    c.funding_amount_sought != null ? String(c.funding_amount_sought) : '',
  valuation: c.valuation != null ? String(c.valuation) : '',
  previous_funding_raw: c.previous_funding_raw || '',
  is_currently_raising:
    c.is_currently_raising === true
      ? 'yes'
      : c.is_currently_raising === false
      ? 'no'
      : 'unknown',
  fundraising_status_reason: c.fundraising_status_reason || '',
  current_revenue: c.current_revenue != null ? String(c.current_revenue) : '',
  monthly_revenue: c.monthly_revenue != null ? String(c.monthly_revenue) : '',
  revenue_growth_rate:
    c.revenue_growth_rate != null ? String(c.revenue_growth_rate) : '',
  gross_margin: c.gross_margin != null ? String(c.gross_margin) : '',
  burn_rate: c.burn_rate != null ? String(c.burn_rate) : '',
  runway_months: c.runway_months != null ? String(c.runway_months) : '',
  customer_acquisition_cost:
    c.customer_acquisition_cost != null
      ? String(c.customer_acquisition_cost)
      : '',
  lifetime_value: c.lifetime_value != null ? String(c.lifetime_value) : '',
  business_model: c.business_model || '',
  target_market: c.target_market || '',
  revenue_model: c.revenue_model || '',
  team_size: c.team_size != null ? String(c.team_size) : '',
  competitive_advantages_csv: arrayToCsv(c.competitive_advantages),
  executive_summary: c.executive_summary || '',
  founder_demographics: c.founder_demographics || '',
  university_affiliations: c.university_affiliations || '',
  pitch_deck_url: c.pitch_deck_url || '',
  group_name: c.group_name || '',
  new_group_name: '',
  group_mode: 'existing',
  tags_csv: arrayToCsv(c.tags),
});

const formToPayload = (form: FormState): Record<string, any> => {
  const groupResolved =
    form.group_mode === 'new'
      ? form.new_group_name.trim() || undefined
      : form.group_name.trim() || undefined;

  const isRaising =
    form.is_currently_raising === 'yes'
      ? true
      : form.is_currently_raising === 'no'
      ? false
      : null;

  return {
    name: form.name.trim(),
    website: trimOrUndef(form.website),
    industry: trimOrUndef(form.industry),
    sub_industry: trimOrUndef(form.sub_industry),
    description: trimOrUndef(form.description),
    founding_date: form.founding_date.trim() || undefined,
    headquarters: trimOrUndef(form.headquarters),
    location_state: trimOrUndef(form.location_state),
    employee_count: toNumber(form.employee_count),
    contact_name: trimOrUndef(form.contact_name),
    contact_email: trimOrUndef(form.contact_email),
    funding_stage: trimOrUndef(form.funding_stage),
    funding_amount_sought: toNumber(form.funding_amount_sought),
    valuation: toNumber(form.valuation),
    previous_funding_raw: trimOrUndef(form.previous_funding_raw),
    is_currently_raising: isRaising,
    fundraising_status_reason: trimOrUndef(form.fundraising_status_reason),
    current_revenue: toNumber(form.current_revenue),
    monthly_revenue: toNumber(form.monthly_revenue),
    revenue_growth_rate: toNumber(form.revenue_growth_rate),
    gross_margin: toNumber(form.gross_margin),
    burn_rate: toNumber(form.burn_rate),
    runway_months: toNumber(form.runway_months),
    customer_acquisition_cost: toNumber(form.customer_acquisition_cost),
    lifetime_value: toNumber(form.lifetime_value),
    business_model: trimOrUndef(form.business_model),
    target_market: trimOrUndef(form.target_market),
    revenue_model: trimOrUndef(form.revenue_model),
    team_size: toNumber(form.team_size),
    competitive_advantages: csvToArray(form.competitive_advantages_csv).length
      ? csvToArray(form.competitive_advantages_csv)
      : undefined,
    executive_summary: trimOrUndef(form.executive_summary),
    founder_demographics: trimOrUndef(form.founder_demographics),
    university_affiliations: trimOrUndef(form.university_affiliations),
    pitch_deck_url: trimOrUndef(form.pitch_deck_url),
    group_name: groupResolved,
    tags: csvToArray(form.tags_csv).length ? csvToArray(form.tags_csv) : undefined,
  };
};

// Drop keys whose value is `undefined` so PUT only sends what the user touched.
const stripUndefined = (obj: Record<string, any>): Record<string, any> => {
  const out: Record<string, any> = {};
  for (const k of Object.keys(obj)) {
    if (obj[k] !== undefined) out[k] = obj[k];
  }
  return out;
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {children}
  </section>
);

const labelClass = 'block text-xs font-medium text-gray-700 mb-1';
const inputClass =
  'block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500';

const NewCompanyPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const companyId = editId ? Number(editId) : null;
  const isEditMode = companyId !== null && !Number.isNaN(companyId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [availableGroups, setAvailableGroups] = useState<
    Array<{ name: string; count: number }>
  >([]);
  const [uploadingDeck, setUploadingDeck] = useState(false);
  const deckInputRef = useRef<HTMLInputElement | null>(null);

  // Load existing company (edit mode).
  useEffect(() => {
    if (!isEditMode || companyId === null) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const r = await api.get(`/companies/${companyId}`);
        if (!cancelled && r.data) {
          setForm(companyToForm(r.data));
        }
      } catch (e: any) {
        if (!cancelled) {
          toast.error(
            e?.response?.data?.detail || 'Failed to load company'
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, companyId]);

  // Load available groups for the picker.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const r = await dataService.fetchCompanyGroups();
      if (!cancelled && r.data) {
        setAvailableGroups(r.data);
        if (r.data.length === 0) {
          setForm((prev) => ({ ...prev, group_mode: 'new' }));
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Upload a pitch deck / brochure PDF for the company being edited.
  // Uses the same endpoint as the Pitch Deck tab: it stores the file,
  // sets pitch_deck_url, and kicks off Claude Vision analysis + blank-
  // field profile extraction. Only available in edit mode — a fresh
  // company has no id to attach the file to yet, so we ask the user to
  // save first.
  const handleDeckUpload = async (file: File) => {
    if (!file) return;
    if (
      !file.name.toLowerCase().endsWith('.pdf') &&
      file.type !== 'application/pdf'
    ) {
      toast.error('Pitch deck / brochure must be a PDF');
      return;
    }
    if (companyId === null) {
      toast.error('Save the company first, then upload a deck');
      return;
    }
    setUploadingDeck(true);
    try {
      const path = `/companies/${companyId}/pitch-deck/upload`;
      const cfg = {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240_000,
      };
      const makeForm = () => {
        const fd = new FormData();
        fd.append('file', file);
        return fd;
      };
      // Prefer apiDirect (bypasses Netlify's ~6 MB cap). On a no-response
      // network/CORS failure, fall back to the same-origin proxied api
      // (works for decks under the proxy cap). Mirrors PitchDeckTab.
      let res;
      try {
        res = await apiDirect.post(path, makeForm(), cfg);
      } catch (directErr: any) {
        // Only a real HTTP status means "don't retry"; the apiDirect
        // interceptor synthesizes a status-less response for no-response
        // / CORS failures, which should fall back to the proxy.
        if (directErr?.response?.status) throw directErr;
        res = await api.post(path, makeForm(), cfg);
      }
      const served = res.data?.pitch_deck_url;
      if (served) updateField('pitch_deck_url', served);
      // Analysis + profile auto-fill now run in the background (~30-60s),
      // so the just-returned response has no extracted fields yet. Tell
      // the user to refresh shortly to see auto-filled fields.
      toast.success(
        'Deck uploaded. Claude analysis is running (~30-60s) — reopen this company shortly to see auto-filled fields.'
      );
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Deck upload failed');
    } finally {
      setUploadingDeck(false);
      if (deckInputRef.current) deckInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setIsSubmitting(true);
    const payload = stripUndefined(formToPayload(form));
    try {
      if (isEditMode && companyId !== null) {
        const r = await api.put(`/companies/${companyId}`, payload);
        toast.success('Company updated');
        navigate(`/companies/${r.data?.id ?? companyId}`);
      } else {
        const r = await api.post('/companies', payload);
        toast.success('Company created');
        navigate(`/companies/${r.data?.id}`);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      // 409 duplicate guard from POST /companies returns a structured
      // body with existing_company_id — surface the option to open the
      // existing record instead of forcing the user to start over.
      if (
        err?.response?.status === 409 &&
        detail &&
        typeof detail === 'object' &&
        detail.existing_company_id
      ) {
        const existingId = detail.existing_company_id;
        const existingName = detail.existing_company_name || 'existing record';
        toast(
          (t) => (
            <div className="text-sm">
              <div className="font-medium mb-1">
                Possible duplicate: {existingName}
              </div>
              <div className="text-xs text-gray-600 mb-2">
                {detail.message}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    toast.dismiss(t.id);
                    navigate(`/companies/${existingId}`);
                  }}
                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs"
                >
                  Open existing
                </button>
                <button
                  onClick={async () => {
                    toast.dismiss(t.id);
                    try {
                      const r2 = await api.post(
                        '/companies?force=true',
                        payload
                      );
                      toast.success('Created anyway');
                      navigate(`/companies/${r2.data?.id}`);
                    } catch (err2: any) {
                      toast.error(
                        err2?.response?.data?.detail || 'Failed to create'
                      );
                    }
                  }}
                  className="px-2 py-1 bg-white border border-gray-300 rounded text-xs"
                >
                  Create anyway
                </button>
              </div>
            </div>
          ),
          { duration: 12000 }
        );
      } else {
        toast.error(
          (typeof detail === 'string' && detail) ||
            detail?.message ||
            'Failed to save company'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading company...
        </div>
      </DashboardLayout>
    );
  }

  const cancelHref =
    isEditMode && companyId !== null
      ? `/companies/${companyId}`
      : '/companies';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div>
          <Link
            to={cancelHref}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Cancel
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit company' : 'Add company'}
          </h1>
          <p className="text-gray-600 mt-1 text-sm">
            Edit profile fields directly. For bulk auto-fill from a pitch deck or
            brochure,{' '}
            <Link
              to={
                isEditMode && companyId !== null
                  ? `/companies/upload-brochure?company_id=${companyId}`
                  : '/companies/upload-brochure'
              }
              className="text-blue-600 hover:underline"
            >
              upload a brochure
            </Link>{' '}
            instead — Claude will extract the structured fields automatically.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Section title="Identity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelClass}>Company name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Website</label>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={form.website}
                  onChange={(e) => updateField('website', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Founding date</label>
                <input
                  type="date"
                  value={form.founding_date}
                  onChange={(e) => updateField('founding_date', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Industry</label>
                <input
                  type="text"
                  placeholder="Fintech, Biotech, Climate…"
                  value={form.industry}
                  onChange={(e) => updateField('industry', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Sub-industry</label>
                <input
                  type="text"
                  value={form.sub_industry}
                  onChange={(e) => updateField('sub_industry', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Headquarters</label>
                <input
                  type="text"
                  placeholder="San Francisco, CA, US"
                  value={form.headquarters}
                  onChange={(e) => updateField('headquarters', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>State / region</label>
                <input
                  type="text"
                  value={form.location_state}
                  onChange={(e) =>
                    updateField('location_state', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Employee count</label>
                <input
                  type="number"
                  min={0}
                  value={form.employee_count}
                  onChange={(e) =>
                    updateField('employee_count', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Team size</label>
                <input
                  type="number"
                  min={0}
                  value={form.team_size}
                  onChange={(e) => updateField('team_size', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Primary contact name</label>
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={(e) => updateField('contact_name', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Primary contact email</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => updateField('contact_email', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Description</label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          <Section title="Funding">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Funding stage</label>
                <select
                  value={form.funding_stage}
                  onChange={(e) =>
                    updateField('funding_stage', e.target.value)
                  }
                  className={inputClass}
                >
                  <option value="">— Select —</option>
                  {FUNDING_STAGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Currently raising?</label>
                <select
                  value={form.is_currently_raising}
                  onChange={(e) =>
                    updateField(
                      'is_currently_raising',
                      e.target.value as FormState['is_currently_raising']
                    )
                  }
                  className={inputClass}
                >
                  <option value="unknown">Unknown</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Amount raising (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.funding_amount_sought}
                  onChange={(e) =>
                    updateField('funding_amount_sought', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Target valuation (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.valuation}
                  onChange={(e) => updateField('valuation', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Previous funding (free text, e.g. "$1.5M pre-seed (2023)")
                </label>
                <input
                  type="text"
                  value={form.previous_funding_raw}
                  onChange={(e) =>
                    updateField('previous_funding_raw', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Fundraising status notes
                </label>
                <textarea
                  rows={2}
                  value={form.fundraising_status_reason}
                  onChange={(e) =>
                    updateField('fundraising_status_reason', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          <Section title="Financials">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Annual revenue (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.current_revenue}
                  onChange={(e) =>
                    updateField('current_revenue', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Monthly revenue (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.monthly_revenue}
                  onChange={(e) =>
                    updateField('monthly_revenue', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Revenue growth rate (decimal, e.g. 0.5 = 50%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.revenue_growth_rate}
                  onChange={(e) =>
                    updateField('revenue_growth_rate', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  Gross margin (decimal 0–1)
                </label>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step="0.01"
                  value={form.gross_margin}
                  onChange={(e) => updateField('gross_margin', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Monthly burn rate (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.burn_rate}
                  onChange={(e) => updateField('burn_rate', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Runway (months)</label>
                <input
                  type="number"
                  min={0}
                  value={form.runway_months}
                  onChange={(e) =>
                    updateField('runway_months', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>CAC (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.customer_acquisition_cost}
                  onChange={(e) =>
                    updateField('customer_acquisition_cost', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>LTV (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.lifetime_value}
                  onChange={(e) =>
                    updateField('lifetime_value', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          <Section title="Operations">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className={labelClass}>Business model</label>
                <textarea
                  rows={2}
                  value={form.business_model}
                  onChange={(e) =>
                    updateField('business_model', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Target market</label>
                <textarea
                  rows={2}
                  value={form.target_market}
                  onChange={(e) =>
                    updateField('target_market', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Revenue model</label>
                <input
                  type="text"
                  placeholder="subscription | transactional | licensing | …"
                  value={form.revenue_model}
                  onChange={(e) =>
                    updateField('revenue_model', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Competitive advantages (comma-separated)
                </label>
                <input
                  type="text"
                  value={form.competitive_advantages_csv}
                  onChange={(e) =>
                    updateField('competitive_advantages_csv', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          <Section title="Context">
            <div>
              <label className={labelClass}>Executive summary</label>
              <textarea
                rows={4}
                value={form.executive_summary}
                onChange={(e) =>
                  updateField('executive_summary', e.target.value)
                }
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Founder demographics</label>
                <input
                  type="text"
                  value={form.founder_demographics}
                  onChange={(e) =>
                    updateField('founder_demographics', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>University affiliations</label>
                <input
                  type="text"
                  value={form.university_affiliations}
                  onChange={(e) =>
                    updateField('university_affiliations', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Pitch deck / brochure</label>
                {/* File upload — stores the PDF, sets the URL below, runs
                    Claude analysis + auto-fills blank profile fields. */}
                <div
                  onClick={() => {
                    if (companyId === null) {
                      toast.error('Save the company first, then upload a deck');
                      return;
                    }
                    if (!uploadingDeck) deckInputRef.current?.click();
                  }}
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    companyId === null
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                      : 'border-gray-300 hover:border-blue-400 cursor-pointer'
                  }`}
                >
                  <input
                    ref={deckInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleDeckUpload(f);
                    }}
                  />
                  {uploadingDeck ? (
                    <div className="flex items-center justify-center text-gray-600 text-sm">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin text-blue-600" />
                      Uploading &amp; analyzing… (up to ~1 min)
                    </div>
                  ) : form.pitch_deck_url ? (
                    <div className="flex items-center justify-center text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                      Deck on file —{' '}
                      <a
                        href={form.pitch_deck_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:underline mx-1"
                      >
                        open
                      </a>
                      · click to replace
                    </div>
                  ) : companyId === null ? (
                    <div className="flex items-center justify-center text-sm text-gray-400">
                      <FileText className="w-4 h-4 mr-2" />
                      Save the company first, then upload a pitch deck / brochure
                    </div>
                  ) : (
                    <div className="flex items-center justify-center text-sm text-gray-600">
                      <UploadCloud className="w-4 h-4 mr-2 text-blue-600" />
                      Click to upload a pitch deck / brochure PDF (max 25 MB)
                    </div>
                  )}
                </div>
                <label className={`${labelClass} mt-3`}>
                  …or paste a pitch deck URL
                </label>
                <input
                  type="url"
                  placeholder="https://…"
                  value={form.pitch_deck_url}
                  onChange={(e) =>
                    updateField('pitch_deck_url', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          <Section title="Organization">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={labelClass}>Group</label>
                  {availableGroups.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        updateField(
                          'group_mode',
                          form.group_mode === 'existing' ? 'new' : 'existing'
                        )
                      }
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {form.group_mode === 'existing'
                        ? '+ Create new group'
                        : 'Pick existing'}
                    </button>
                  )}
                </div>
                {form.group_mode === 'existing' ? (
                  <select
                    value={form.group_name}
                    onChange={(e) =>
                      updateField('group_name', e.target.value)
                    }
                    className={inputClass}
                  >
                    <option value="">No group</option>
                    {availableGroups.map((g) => (
                      <option key={g.name} value={g.name}>
                        {g.name} ({g.count})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="e.g. MTW Cohort 4"
                    value={form.new_group_name}
                    onChange={(e) =>
                      updateField('new_group_name', e.target.value)
                    }
                    className={inputClass}
                  />
                )}
              </div>
              <div>
                <label className={labelClass}>Tags (comma-separated)</label>
                <input
                  type="text"
                  placeholder="warm intro, follow-up Q3"
                  value={form.tags_csv}
                  onChange={(e) => updateField('tags_csv', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          <div className="flex items-center justify-end gap-3 sticky bottom-4">
            <Link
              to={cancelHref}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEditMode ? 'Save changes' : 'Create company'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default NewCompanyPage;
